// The approved design's charts (design/ai-bookkeeper-premium.html), drawn from
// the numbers they are given and nothing else.
//
// Each chart is an SVG that is aria-hidden, with the same numbers stated in
// text beside it (a caption, a legend, or a visually hidden table): a chart a
// screen reader cannot read is a figure half the reviewers cannot check, and
// tests assert on that text rather than on path coordinates.
//
// No chart invents a shape. A series of zeroes draws a flat line, and the
// screens show their empty state instead of drawing one at all.

import { useEffect, useState, type ReactNode } from 'react';

export const CHART_COLOURS = {
  ink: '#1D3B4F',
  green: '#1F7A4D',
  amber: '#9A5B0B',
  blue: '#2556B0',
  grey: '#B8C0CC',
  faint: '#9AA8B4',
  track: '#EEF0F3',
} as const;

/**
 * The design's spend colours, in its order. The fourth, grey, is the design's
 * "Other": SpendCard gives each ledger its own segment up to four, and folds
 * the rest into one grey "Other" rather than inventing more colours.
 */
const SLICE_COLOURS = [CHART_COLOURS.ink, CHART_COLOURS.green, CHART_COLOURS.blue, CHART_COLOURS.grey];

/**
 * Whether to animate at all: only where the browser can say the person has not
 * asked for less motion. Without matchMedia (tests, very old browsers) the
 * answer is no — the final figure is shown at once, which is the honest frame.
 */
export function motionAllowed(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * A figure that counts up to its value on first show, as the design's
 * `countUp` (1.3 s, ease-out cubic). Only the number moves; what it counts to
 * is the value given, and under reduced motion that is all that is shown.
 */
export function CountUp({
  value,
  format,
}: {
  readonly value: number;
  readonly format: (value: number) => string;
}): ReactNode {
  const [shown, setShown] = useState(() => (motionAllowed() ? 0 : value));
  useEffect(() => {
    if (!motionAllowed()) {
      setShown(value);
      return undefined;
    }
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - started) / 1300);
      setShown(value * (1 - Math.pow(1 - k, 3)));
      if (k < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return (
    <span aria-label={format(value)}>
      <span aria-hidden="true">{format(shown === value ? value : shown)}</span>
    </span>
  );
}

export function Sparkline({
  values,
  colour = CHART_COLOURS.faint,
}: {
  readonly values: readonly number[];
  readonly colour?: string;
}): ReactNode {
  if (values.length < 2) return null;
  const width = 84;
  const height = 30;
  const max = Math.max(...values) || 1;
  const step = width / (values.length - 1);
  const points = values
    .map((value, i) => `${(i * step).toFixed(1)},${(height - 3 - (value / max) * (height - 6)).toFixed(1)}`)
    .join(' ');
  return (
    <svg className="spk" viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
      <polyline
        points={points}
        fill="none"
        stroke={colour}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface Series {
  readonly label: string;
  readonly colour: string;
  readonly values: readonly number[];
  readonly dashed?: boolean;
}

// Dutch abbreviations, as format.ts writes them.
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/** '2026-10-20' → '20 okt', without a Date (and so without a time zone). */
export function shortDay(day: string): string {
  const [, month, date] = day.split('-');
  return `${Number(date)} ${MONTHS[Number(month) - 1] ?? ''}`;
}

/**
 * Two series over the same days, the first as a filled line and the second
 * dashed. The hover tooltip states every series for that day; the hidden table
 * states every day.
 */
export function LineChart({
  days,
  series,
  caption,
}: {
  readonly days: readonly string[];
  readonly series: readonly Series[];
  readonly caption: string;
}): ReactNode {
  const [hover, setHover] = useState<number | null>(null);
  const W = 640;
  const H = 210;
  const pl = 30;
  const pr = 8;
  const pt = 10;
  const pb = 26;
  const peak = Math.max(1, ...series.flatMap((s) => s.values));
  const max = Math.max(4, Math.ceil((peak * 1.2) / 4) * 4);
  const step = days.length > 1 ? (W - pl - pr) / (days.length - 1) : 0;
  const x = (i: number) => pl + i * step;
  const y = (v: number) => pt + (H - pt - pb) * (1 - v / max);
  const path = (values: readonly number[]) => {
    let d = `M${x(0)},${y(values[0] ?? 0)}`;
    for (let i = 1; i < values.length; i += 1) {
      const cx = (x(i - 1) + x(i)) / 2;
      d += ` C${cx},${y(values[i - 1] ?? 0)} ${cx},${y(values[i] ?? 0)} ${x(i)},${y(values[i] ?? 0)}`;
    }
    return d;
  };
  const first = series[0];
  const labelEvery = Math.ceil(days.length / 6);

  return (
    <div className="cw">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        aria-hidden="true"
        focusable="false"
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          if (box.width === 0 || step === 0) return;
          const sx = (event.clientX - box.left) * (W / box.width);
          setHover(Math.max(0, Math.min(days.length - 1, Math.round((sx - pl) / step))));
        }}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="chart-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={first?.colour ?? CHART_COLOURS.ink} stopOpacity=".14" />
            <stop offset="1" stopColor={first?.colour ?? CHART_COLOURS.ink} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((k) => {
          const value = (max * k) / 4;
          return (
            <g key={k}>
              <line x1={pl} x2={W - pr} y1={y(value)} y2={y(value)} stroke={CHART_COLOURS.track} />
              <text x={pl - 8} y={y(value) + 4} textAnchor="end" fontSize="10" fill={CHART_COLOURS.faint}>
                {Math.round(value)}
              </text>
            </g>
          );
        })}
        {days.map((day, i) =>
          i % labelEvery === 0 ? (
            <text key={day} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill={CHART_COLOURS.faint}>
              {shortDay(day)}
            </text>
          ) : null,
        )}
        {first === undefined ? null : (
          <path
            className="chart-area"
            d={`${path(first.values)} L${x(days.length - 1)},${y(0)} L${x(0)},${y(0)} Z`}
            fill="url(#chart-fill)"
          />
        )}
        {series.map((s) => (
          <path
            key={s.label}
            // The design draws the solid line in and fades the dashed one.
            className={s.dashed ? 'chart-fade' : 'chart-draw'}
            {...(s.dashed ? {} : { pathLength: 1 })}
            d={path(s.values)}
            fill="none"
            stroke={s.colour}
            strokeWidth={s.dashed ? 2 : 2.4}
            strokeLinecap="round"
            {...(s.dashed ? { strokeDasharray: '5 5' } : {})}
          />
        ))}
        {hover === null || first === undefined ? null : (
          <>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={pt}
              y2={H - pb}
              stroke={CHART_COLOURS.ink}
              strokeOpacity=".25"
              strokeDasharray="3 3"
            />
            <circle cx={x(hover)} cy={y(first.values[hover] ?? 0)} r="5" fill={first.colour} stroke="#fff" strokeWidth="2" />
          </>
        )}
      </svg>
      {hover === null ? null : (
        <div
          className="tip show"
          style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(first?.values[hover] ?? 0) / H) * 100}%` }}
          aria-hidden="true"
        >
          {shortDay(days[hover] ?? '')} ·{' '}
          {series.map((s, i) => (
            <span key={s.label}>
              {i > 0 ? ' · ' : ''}
              <b>{s.values[hover] ?? 0}</b> {s.label.toLowerCase()}
            </span>
          ))}
        </div>
      )}
      <table className="visually-hidden">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            {series.map((s) => (
              <th scope="col" key={s.label}>
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((day, i) => (
            <tr key={day}>
              <th scope="row">{day}</th>
              {series.map((s) => (
                <td key={s.label}>{s.values[i] ?? 0}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A share drawn as a ring, with the label in the middle. `share` is 0–1. */
export function Ring({
  share,
  label,
  sub,
  colour = CHART_COLOURS.green,
  size = 112,
}: {
  readonly share: number;
  readonly label: string;
  readonly sub?: string;
  readonly colour?: string;
  readonly size?: number;
}): ReactNode {
  const r = size / 2 - 8;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, share));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
      <circle cx={c} cy={c} r={r} fill="none" stroke={CHART_COLOURS.track} strokeWidth="9" />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke={colour}
        strokeWidth="9"
        strokeLinecap="round"
        transform={`rotate(-90 ${c} ${c})`}
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped)}
        className="ring-draw"
        style={{ ['--from' as string]: String(circumference) }}
      />
      <text x={c} y={c + (sub === undefined ? 6 : 1)} textAnchor="middle" fontSize="22" fontWeight="700" fill={CHART_COLOURS.ink}>
        {label}
      </text>
      {sub === undefined ? null : (
        <text x={c} y={c + 17} textAnchor="middle" fontSize="10" fill={CHART_COLOURS.faint}>
          {sub}
        </text>
      )}
    </svg>
  );
}

export interface Slice {
  readonly label: string;
  /** A non-negative number, for proportions only. Amounts are shown from their exact text. */
  readonly weight: number;
}

/** Slices in the order given; the caller states the exact amounts in its legend. */
export function Donut({
  slices,
  centre,
  sub,
}: {
  readonly slices: readonly Slice[];
  readonly centre: string;
  readonly sub?: string;
}): ReactNode {
  const r = 46;
  const ring = 10;
  // The centre label is sized to the hole: about 0.58 em per character in
  // Inter at weight 700, inside the inner diameter less a margin.
  const hole = 2 * (r - ring / 2) - 10;
  const centreSize = Math.max(9, Math.min(15, hole / (0.58 * Math.max(1, centre.length))));
  const circumference = 2 * Math.PI * r;
  const total = slices.reduce((sum, slice) => sum + Math.max(0, slice.weight), 0);
  let offset = 0;
  return (
    <svg width="112" height="112" viewBox="0 0 112 112" aria-hidden="true" focusable="false">
      <circle cx="56" cy="56" r={r} fill="none" stroke={CHART_COLOURS.track} strokeWidth={ring} />
      {total === 0
        ? null
        : slices.map((slice, i) => {
            const length = (circumference * Math.max(0, slice.weight)) / total;
            const element = (
              <circle
                key={slice.label}
                cx="56"
                cy="56"
                r={r}
                fill="none"
                stroke={sliceColour(i)}
                strokeWidth={ring}
                strokeDasharray={`${Math.max(0, length - (slices.length > 1 ? 2 : 0))} ${circumference}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 56 56)"
                className="slice-draw"
                style={{ ['--gap' as string]: String(circumference), animationDelay: `${i * 0.18}s` }}
              />
            );
            offset += length;
            return element;
          })}
      <text x="56" y="55" textAnchor="middle" fontSize={centreSize} fontWeight="700" fill={CHART_COLOURS.ink} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {centre}
      </text>
      {sub === undefined ? null : (
        <text x="56" y="69" textAnchor="middle" fontSize="10" fill={CHART_COLOURS.faint}>
          {sub}
        </text>
      )}
    </svg>
  );
}

export function sliceColour(index: number): string {
  return SLICE_COLOURS[index] ?? CHART_COLOURS.grey;
}

/**
 * Paired bars per period (e.g. offered and approved per week), with the values
 * printed above each bar and repeated in a hidden table.
 */
export function BarChart({
  labels,
  series,
  caption,
}: {
  readonly labels: readonly string[];
  readonly series: readonly { readonly label: string; readonly colour: string; readonly values: readonly number[] }[];
  readonly caption: string;
}): ReactNode {
  const W = 320;
  const H = 150;
  const pb = 22;
  const pt = 14;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const group = W / Math.max(1, labels.length);
  const bar = Math.min(18, (group - 10) / Math.max(1, series.length));
  return (
    <div className="cw">
      <svg viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
        {labels.map((label, i) => {
          const x0 = i * group + (group - bar * series.length) / 2;
          return (
            <g key={label}>
              {series.map((s, j) => {
                const value = s.values[i] ?? 0;
                const h = ((H - pt - pb) * value) / max;
                const x = x0 + j * bar;
                return (
                  <g key={s.label}>
                    <rect x={x + 1} y={H - pb - h} width={bar - 2} height={h} rx="3" fill={s.colour} />
                    {value === 0 ? null : (
                      <text x={x + bar / 2} y={H - pb - h - 3} textAnchor="middle" fontSize="9" fill={CHART_COLOURS.faint}>
                        {value}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={i * group + group / 2} y={H - 6} textAnchor="middle" fontSize="10" fill={CHART_COLOURS.faint}>
                {label}
              </text>
            </g>
          );
        })}
      </svg>
      <table className="visually-hidden">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            {series.map((s) => (
              <th scope="col" key={s.label}>
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((label, i) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              {series.map((s) => (
                <td key={s.label}>{s.values[i] ?? 0}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Two counts per day stacked as one bar (the first at the bottom), for the
 * first weeks of use, before there is enough history for a trend line. Every
 * day is a slot; a day with nothing is the baseline and nothing else. The hover
 * tooltip states the day and both counts, and the hidden table states every
 * day.
 */
export function StackedBars({
  days,
  series,
  caption,
}: {
  readonly days: readonly string[];
  readonly series: readonly [Series, Series];
  readonly caption: string;
}): ReactNode {
  const [hover, setHover] = useState<number | null>(null);
  const W = 640;
  const H = 210;
  const pl = 30;
  const pr = 8;
  const pt = 10;
  const pb = 26;
  const totals = days.map((_, i) => series[0].values[i] ?? 0).map((v, i) => v + (series[1].values[i] ?? 0));
  const peak = Math.max(0, ...totals);
  const max = Math.max(4, Math.ceil((peak * 1.2) / 4) * 4);
  const slot = (W - pl - pr) / Math.max(1, days.length);
  const bar = Math.max(3, Math.min(26, slot * 0.62));
  const y = (v: number) => pt + (H - pt - pb) * (1 - v / max);
  const base = y(0);
  const labelEvery = Math.ceil(days.length / 7);
  const top = hover === null ? 0 : y(totals[hover] ?? 0);
  return (
    <div className="cw">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        aria-hidden="true"
        focusable="false"
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          if (box.width === 0) return;
          const sx = (event.clientX - box.left) * (W / box.width);
          setHover(Math.max(0, Math.min(days.length - 1, Math.floor((sx - pl) / slot))));
        }}
        onMouseLeave={() => setHover(null)}
      >
        {[0, 1, 2, 3, 4].map((k) => {
          const value = (max * k) / 4;
          return (
            <g key={k}>
              <line x1={pl} x2={W - pr} y1={y(value)} y2={y(value)} stroke={CHART_COLOURS.track} />
              <text x={pl - 8} y={y(value) + 4} textAnchor="end" fontSize="10" fill={CHART_COLOURS.faint}>
                {Math.round(value)}
              </text>
            </g>
          );
        })}
        {hover === null ? null : (
          <rect x={pl + hover * slot} y={pt} width={slot} height={H - pt - pb} fill={CHART_COLOURS.ink} fillOpacity=".04" />
        )}
        {days.map((day, i) => {
          const x = pl + i * slot + (slot - bar) / 2;
          const lower = series[0].values[i] ?? 0;
          const upper = series[1].values[i] ?? 0;
          const lowerTop = y(lower);
          const upperTop = y(lower + upper);
          // Both parts of a bar grow from the baseline together, day by day.
          const grow = { animationDelay: `${Math.min(i, 30) * 0.02}s`, transformOrigin: `0 ${base}px` };
          return (
            <g key={day}>
              {lower === 0 ? null : (
                <rect className="bar-grow" style={grow} x={x} y={lowerTop} width={bar} height={base - lowerTop} rx={Math.min(4, bar / 3)} fill={series[0].colour} />
              )}
              {upper === 0 ? null : (
                <rect className="bar-grow" style={grow} x={x} y={upperTop} width={bar} height={lowerTop - upperTop - (lower === 0 ? 0 : 1.5)} rx={Math.min(4, bar / 3)} fill={series[1].colour} />
              )}
              {i % labelEvery === 0 ? (
                <text x={pl + i * slot + slot / 2} y={H - 6} textAnchor="middle" fontSize="10" fill={CHART_COLOURS.faint}>
                  {shortDay(day)}
                </text>
              ) : null}
            </g>
          );
        })}
        <line x1={pl} x2={W - pr} y1={base} y2={base} stroke={CHART_COLOURS.grey} />
      </svg>
      {hover === null ? null : (
        <div
          className="tip show"
          style={{ left: `${((pl + hover * slot + slot / 2) / W) * 100}%`, top: `${(top / H) * 100}%` }}
          aria-hidden="true"
        >
          {shortDay(days[hover] ?? '')} ·{' '}
          {series.map((s, i) => (
            <span key={s.label}>
              {i > 0 ? ' · ' : ''}
              <b>{s.values[hover] ?? 0}</b> {s.label.toLowerCase()}
            </span>
          ))}
        </div>
      )}
      <table className="visually-hidden">
        <caption>{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Day</th>
            {series.map((s) => (
              <th scope="col" key={s.label}>
                {s.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {days.map((day, i) => (
            <tr key={day}>
              <th scope="row">{day}</th>
              {series.map((s) => (
                <td key={s.label}>{s.values[i] ?? 0}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
